/**
 * AudioNotificationsCard.jsx
 *
 * Modal card the user opens from the speaker-icon button next to Tamam
 * al-Salah. Phase 1 of the Audio Notifications feature: in-app audio
 * only (honest banner explains the current limitation).
 *
 * Uses its own fixed high-contrast palette (independent of sky phase)
 * so text stays readable over the dark backdrop at any time of day.
 */

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Volume2, Play, Pause, Check, FileAudio, Info, X, AlertCircle } from 'lucide-react';
import { tt } from '@/utils/localizedStrings';
import { AUDIO_STRINGS } from '@/utils/audioStrings';
import {
  getSettings,
  setSettings,
  saveTone,
  loadTone,
  playBlob,
  stopPlayback,
  isPlaying,
} from '@/utils/audioNotifications';
import { BackgroundPushSection } from '@/components/BackgroundPushSection';

const P = {
  card: '#131B2E',
  border: 'rgba(255,255,255,0.16)',
  field: 'rgba(255,255,255,0.07)',
  primary: '#FFFFFF',
  secondary: '#DCE4EF',
  muted: '#AEBBCD',
  accent: '#FCD34D',
  accentInk: '#1A1204',
};

const formatSize = (bytes, lang) => {
  const mb = bytes / (1024 * 1024);
  const useArabic = lang === 'ar' || lang === 'ur' || lang === 'fa';
  const dec = mb.toFixed(1);
  if (useArabic) {
    const arabicDigits = dec.replace(/[0-9]/g, (d) => '٠١٢٣٤٥٦٧٨٩'[+d]);
    return `${arabicDigits} م.ب`;
  }
  return `${dec} MB`;
};

export default function AudioNotificationsCard({ open, onClose, lang, isRTL, pushParams }) {
  const [volume, setVolume] = useState(0.65);
  const [repeat, setRepeat] = useState(2);
  const [toneMeta, setToneMeta] = useState(null); // { name, size, type }
  const [toneBlob, setToneBlob] = useState(null);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  const fileInputRef = useRef(null);

  // Load saved settings + tone when the card opens.
  useEffect(() => {
    if (!open) return;
    const s = getSettings();
    setVolume(s.volume);
    setRepeat(s.repeat);
    (async () => {
      const t = await loadTone();
      if (t) {
        setToneMeta({ name: t.name, size: t.size, type: t.type });
        setToneBlob(t.blob);
      } else {
        setToneMeta(null);
        setToneBlob(null);
      }
    })();
    setError(null);
  }, [open]);

  // Stop any ongoing playback when the modal is closed.
  useEffect(() => {
    if (!open && isPlaying()) {
      stopPlayback();
      setPlaying(false);
    }
  }, [open]);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setError(null);
    try {
      const meta = await saveTone(file);
      setToneMeta(meta);
      setToneBlob(file);
    } catch (err) {
      if (err.message === 'too_large') setError(tt(AUDIO_STRINGS.errorTooLarge, lang));
      else setError(tt(AUDIO_STRINGS.errorInvalidType, lang));
    }
    // allow re-picking the same file
    e.target.value = '';
  };

  const handleTest = () => {
    if (!toneBlob) return;
    if (playing) {
      stopPlayback();
      setPlaying(false);
      return;
    }
    playBlob(toneBlob, { volume, repeat });
    setPlaying(true);
    const tick = setInterval(() => {
      if (!isPlaying()) {
        setPlaying(false);
        clearInterval(tick);
      }
    }, 400);
  };

  const handleSave = () => {
    setSettings({ volume, repeat, enabled: Boolean(toneBlob) });
    stopPlayback();
    setPlaying(false);
    onClose && onClose();
  };

  const handleClose = () => {
    stopPlayback();
    setPlaying(false);
    onClose && onClose();
  };

  if (!open) return null;

  const volumePct = Math.round(volume * 100);
  const volumeArabic = (lang === 'ar' || lang === 'ur' || lang === 'fa')
    ? `${String(volumePct).replace(/[0-9]/g, (d) => '٠١٢٣٤٥٦٧٨٩'[+d])}٪`
    : `${volumePct}%`;

  const labelStyle = { color: P.secondary, fontSize: 14, fontWeight: 600, marginBottom: 8 };
  const hintStyle = { color: P.muted, fontSize: 12, lineHeight: 1.5 };
  const openPicker = () => fileInputRef.current && fileInputRef.current.click();

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
      onClick={handleClose}
      data-testid="audio-notifications-backdrop"
    >
      <div
        className="rounded-2xl p-5 w-full max-w-sm"
        style={{
          background: P.card,
          border: `1px solid ${P.border}`,
          boxShadow: '0 20px 50px rgba(0,0,0,0.55)',
          maxHeight: '92vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
        dir={isRTL ? 'rtl' : 'ltr'}
        data-testid="audio-notifications-card"
      >
        {/* Title row */}
        <div className="flex items-center gap-2 mb-4">
          <Volume2 size={24} color={P.accent} />
          <div className="text-heading flex-1 flex items-center gap-2" style={{ color: P.primary, fontSize: 18, fontWeight: 700 }}>
            {tt(AUDIO_STRINGS.title, lang)}
            <span
              className="rounded-full px-2 py-0.5"
              style={{ background: 'rgba(252,211,77,0.18)', color: P.accent, border: '1px solid rgba(252,211,77,0.55)', fontSize: 11, fontWeight: 700 }}
              data-testid="audio-beta-badge"
            >
              {tt(AUDIO_STRINGS.betaBadge, lang)}
            </span>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-full flex items-center justify-center"
            style={{ width: 34, height: 34, background: 'rgba(255,255,255,0.12)', color: P.primary, border: 'none' }}
            aria-label={tt(AUDIO_STRINGS.close, lang)}
            data-testid="audio-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        {/* Honesty banner */}
        <div
          className="rounded-lg p-3 mb-4 flex items-start gap-2"
          style={{ background: 'rgba(245, 158, 11, 0.16)', border: '1px solid rgba(245, 158, 11, 0.5)' }}
          data-testid="audio-honesty-banner"
        >
          <Info size={16} color="#FBBF24" className="mt-0.5 flex-shrink-0" />
          <div style={{ color: '#FEF3C7', fontSize: 13, lineHeight: 1.6 }}>
            {tt(AUDIO_STRINGS.honestyBanner, lang)}
            <div className="mt-2" style={{ fontWeight: 600 }} data-testid="audio-beta-note">
              {tt(AUDIO_STRINGS.betaNote, lang)}
            </div>
          </div>
        </div>

        <BackgroundPushSection lang={lang} isRTL={isRTL} pushParams={pushParams} P={P} />

        {/* Error */}
        {error && (
          <div
            className="rounded-lg p-3 mb-4 flex items-start gap-2"
            style={{ background: 'rgba(239, 68, 68, 0.16)', border: '1px solid rgba(239, 68, 68, 0.55)' }}
            data-testid="audio-error"
          >
            <AlertCircle size={16} color="#F87171" className="mt-0.5 flex-shrink-0" />
            <div style={{ color: '#FEE2E2', fontSize: 13, lineHeight: 1.6 }}>{error}</div>
          </div>
        )}

        {/* Volume slider */}
        <div className="mb-5">
          <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
            <div style={{ ...labelStyle, marginBottom: 0 }}>{tt(AUDIO_STRINGS.volumeLabel, lang)}</div>
            <div style={{ color: P.accent, fontSize: 15, fontWeight: 800 }} data-testid="audio-volume-pct">
              {volumeArabic}
            </div>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={volumePct}
            onChange={(e) => setVolume(Number(e.target.value) / 100)}
            className="w-full h-2 rounded-full appearance-none"
            style={{
              background: `linear-gradient(${isRTL ? 270 : 90}deg, ${P.accent} 0%, ${P.accent} ${volumePct}%, rgba(255,255,255,0.18) ${volumePct}%, rgba(255,255,255,0.18) 100%)`,
              accentColor: P.accent,
            }}
            data-testid="audio-volume-slider"
          />
          <div className="flex justify-between mt-1.5">
            <span style={hintStyle}>{tt(AUDIO_STRINGS.volumeOff, lang)}</span>
            <span style={hintStyle}>{tt(AUDIO_STRINGS.volumeHigh, lang)}</span>
          </div>
          <div className="mt-2 text-center" style={hintStyle}>
            {tt(AUDIO_STRINGS.phoneVolumeNote, lang)}
          </div>
        </div>

        {/* File picker */}
        <div className="mb-5">
          <div style={labelStyle}>
            {toneMeta ? tt(AUDIO_STRINGS.toneSelectedLabel, lang) : tt(AUDIO_STRINGS.toneLabel, lang)}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,.mp3,.wav,.ogg"
            onChange={handleFile}
            className="hidden"
            data-testid="audio-file-input"
          />
          {toneMeta ? (
            <div
              className="w-full rounded-lg p-3 flex items-center gap-2"
              style={{ background: 'rgba(16, 185, 129, 0.14)', border: '1.5px solid rgba(52, 211, 153, 0.7)' }}
              data-testid="audio-tone-selected"
            >
              <Check size={18} color="#34D399" className="flex-shrink-0" />
              <span
                dir="auto"
                style={{
                  color: P.primary,
                  fontSize: 14,
                  fontWeight: 600,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {toneMeta.name}
              </span>
              <span style={{ color: P.muted, fontSize: 12, flex: '0 0 auto' }}>
                {formatSize(toneMeta.size, lang)}
              </span>
              <button
                type="button"
                onClick={openPicker}
                className="rounded-md px-3 py-1"
                style={{ background: 'rgba(255,255,255,0.16)', color: P.primary, fontSize: 12.5, fontWeight: 600, border: 'none' }}
                data-testid="audio-change-btn"
              >
                {tt(AUDIO_STRINGS.changeFile, lang)}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={openPicker}
              className="w-full rounded-lg p-3 flex items-center gap-2"
              style={{ background: P.field, border: `1.5px dashed rgba(255,255,255,0.35)` }}
              data-testid="audio-pick-btn"
            >
              <FileAudio size={18} color={P.secondary} />
              <span style={{ color: P.secondary, fontSize: 14, flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                {tt(AUDIO_STRINGS.pickTone, lang)}
              </span>
              <span
                className="rounded-md px-3 py-1"
                style={{ background: P.accent, color: P.accentInk, fontSize: 12.5, fontWeight: 700 }}
              >
                {tt(AUDIO_STRINGS.browse, lang)}
              </span>
            </button>
          )}
          <div style={{ ...hintStyle, marginTop: 6, textAlign: 'center' }}>
            {tt(AUDIO_STRINGS.localStorageNote, lang)}
          </div>
        </div>

        {/* Repeat count */}
        <div className="mb-5">
          <div style={labelStyle}>{tt(AUDIO_STRINGS.repeatLabel, lang)}</div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { k: 1, label: tt(AUDIO_STRINGS.repeatOnce, lang) },
              { k: 2, label: tt(AUDIO_STRINGS.repeatTwice, lang) },
              { k: 3, label: tt(AUDIO_STRINGS.repeatThrice, lang) },
            ].map((opt) => {
              const active = repeat === opt.k;
              return (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => setRepeat(opt.k)}
                  className="rounded-lg py-2.5 text-center"
                  style={{
                    background: active ? 'rgba(252, 211, 77, 0.18)' : P.field,
                    border: active ? `2px solid ${P.accent}` : `1px solid ${P.border}`,
                    color: active ? P.accent : P.secondary,
                    fontSize: 14,
                    fontWeight: active ? 800 : 500,
                    transition: 'background-color 150ms, border-color 150ms, color 150ms',
                  }}
                  data-testid={`audio-repeat-${opt.k}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleTest}
            disabled={!toneBlob}
            className="flex-1 py-3 rounded-xl flex items-center justify-center gap-2"
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1.5px solid rgba(255,255,255,0.35)',
              color: P.primary,
              fontSize: 15,
              fontWeight: 700,
              opacity: toneBlob ? 1 : 0.45,
              cursor: toneBlob ? 'pointer' : 'not-allowed',
            }}
            data-testid="audio-test-btn"
          >
            {playing ? <Pause size={16} /> : <Play size={16} />}
            {playing ? tt(AUDIO_STRINGS.stop, lang) : tt(AUDIO_STRINGS.test, lang)}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex-1 py-3 rounded-xl text-heading"
            style={{ background: P.accent, color: P.accentInk, fontSize: 15, fontWeight: 800, border: 'none' }}
            data-testid="audio-save-btn"
          >
            {tt(AUDIO_STRINGS.save, lang)}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
